import * as Yup from 'yup';

import { ValidationSchemaObject, YupTypes } from '@/models/ValidationModel';

import regexUtil from '@/utils/regex';
import maskUtil from '@/utils/mask';
import { capitalize } from '@/utils/text';

const processFormatConstraint = (fieldValidation, format, errorField) => {
  const labelText = capitalize(errorField);

  return errorField
    ? fieldValidation[format](`${labelText} must be a valid ${format}`)
    : fieldValidation[format]();
};

const processMatchesConstraint = (fieldValidation, matchProps) => {
  const [pattern, message, formatOnly] = matchProps;
  return fieldValidation.matches(pattern, {
    message: formatOnly ? `Please use this format: ${message}` : message,
    excludeEmptyString: true,
  });
};

const processMaxConstraint = (fieldValidation, matchProps) => {
  const [max, message] = matchProps;
  if (typeof max == 'string') {
    return fieldValidation.min(
      Yup.ref(max),
      message || `Value should not exceed ${max}`
    );
  }

  return fieldValidation.max(max, message || `Value should not exceed ${max}`);
};

const processMinConstraint = (fieldValidation, matchProps) => {
  const [min, message] = matchProps;
  console.log('min', min);
  if (typeof min == 'string') {
    return fieldValidation.min(
      Yup.ref(min),
      message || `Value should not be less than ${min}`
    );
  }

  return fieldValidation.min(
    min,
    message || `Value should not be less than ${min}`
  );
};

const processOneOfConstraint = (fieldValidation, oneOfProps) => {
  const [basisField, errorMessage] = oneOfProps;
  return fieldValidation.oneOf([Yup.ref(basisField), undefined], errorMessage);
};

const processRequiredConstraint = (
  fieldValidation,
  field,
  errorTextFieldLabel
) => {
  let labelText: string | null = null;
  if (errorTextFieldLabel) {
    labelText = capitalize(errorTextFieldLabel);
    return fieldValidation.required(`${labelText} is required`);
  }

  const fieldHasUnderScores = field.includes('_');

  if (fieldHasUnderScores) {
    labelText = capitalize(field.replace(/_/g, ' ').trim());
    return fieldValidation.required(`${labelText} is required`);
  }

  return fieldValidation.required().label(capitalize(field));
};

const processTestConstraint = (fieldValidation, testObject) =>
  fieldValidation.test(testObject);

const processTransformConstraint = (fieldValidation, transformFunction) =>
  fieldValidation.transform(transformFunction);

export const createValidationObjectSchema = (
  validationSchemaObject: ValidationSchemaObject,
  returnYupObject = true
) => {
  const validationObjectSchema = {};

  Object.entries(validationSchemaObject).forEach(
    ([field, validationObject]) => {
      let fieldValidation = Yup[
        // eslint-disable-next-line
        (validationObject.type || YupTypes.STRING) as unknown as any
      ]()
        .label(field)
        .nullable(true)
        .typeError(
          `Please enter a valid ${validationObject.type || YupTypes.STRING}`
        );

      // REQUIRED
      if (validationObject.required) {
        fieldValidation = processRequiredConstraint(
          fieldValidation,
          field,
          validationObject.errorTextFieldLabel
        );
      }

      // FORMAT
      if (validationObject.format) {
        fieldValidation = processFormatConstraint(
          fieldValidation,
          validationObject.format,
          validationObject.errorTextFieldLabel ||
            field
              .split('_')
              .filter((word) => word !== 'id')
              .join(' ')
        );
      }

      // MATCHES
      if (validationObject.match) {
        fieldValidation = processMatchesConstraint(
          fieldValidation,
          validationObject.match
        );
      }

      // TRANSFORM
      if (validationObject.transform) {
        fieldValidation = processTransformConstraint(
          fieldValidation,
          validationObject.transform
        );
      }

      // MAX
      if (validationObject.max) {
        fieldValidation = processMaxConstraint(
          fieldValidation,
          validationObject.max
        );
      }

      // MIN
      if (validationObject.min) {
        fieldValidation = processMinConstraint(
          fieldValidation,
          validationObject.min
        );
      }

      // ONE OF
      if (validationObject.oneOf) {
        fieldValidation = processOneOfConstraint(
          fieldValidation,
          validationObject.oneOf
        );
      }

      // TEST
      if (validationObject.test) {
        fieldValidation = processTestConstraint(
          fieldValidation,
          validationObject.test
        );
      }

      validationObjectSchema[field] = fieldValidation;
    }
  );

  if (!returnYupObject) return validationObjectSchema;

  return Yup.object(validationObjectSchema);
};

export const formatMatches = {
  float: [regexUtil.float, 'Please enter a valid number'],
  alphabet: [regexUtil.alphabet, 'Please enter a valid string'],
  numerical: [regexUtil.numeric, 'Please enter a valid numerical value'],
  number: [regexUtil.numeric, 'Please enter a valid number'],
  phone: {
    generic: [regexUtil.phone.generic, 'XXXXXXXXXX', true],
    aus: [regexUtil.phone.aus, maskUtil.phone.aus, true],
    php: [regexUtil.phone.php, 'XXXX XXX XXX', true],
  },
  mobile: {
    generic: [regexUtil.mobile.generic, 'XXXXXXXXXX', true],
    aus: [regexUtil.mobile.aus, maskUtil.mobile.aus, true],
    php: [regexUtil.mobile.php, 'XXXX XXX XXX', true],
  },
  postalCode: {
    aus: [regexUtil.postalCode.aus, 'XXXX', true],
    php: [regexUtil.postalCode.php, 'XXXX', true],
  },
  voip: [regexUtil.voip, 'XX XXXX XXXX', true],
};

export const reusables = {
  fields: {
    todayMinDate: [new Date(), "You can't use any date on or before today"],
  },
  forms: {
    accountDetails: {
      email: {
        format: 'email',
        required: true,
      },
      password: {
        required: true,
        match: [
          regexUtil.password,
          'The password does not match the requirements',
        ],
      },
      password_confirmation: {
        required: true,
        oneOf: ['password', 'Passwords must match'],
      },
    },
  },
};

export const transformations = {
  stringToDate: (curr: string) => {
    if (!curr || !curr.length) return new Date();

    return new Date(curr);
  },
  numberTo2Decimal: (number: string | number) => {
    const stringNumber = number.toString();

    return parseFloat(stringNumber).toFixed(2);
  },
};
